import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api/client";
import { resetDevData, runDevFplPoll, runDevTestEmail } from "../../api/dev";
import { card, secondaryBtn, pageTitle } from "../../styles";

export default function DevPage() {
  const { t } = useTranslation();

  const [resetting, setResetting] = useState(false);
  const [resetStatus, setResetStatus] = useState<"idle" | "ok" | "err">("idle");

  const [polling, setPolling] = useState(false);
  const [pollStatus, setPollStatus] = useState<"idle" | "ok" | "err">("idle");
  const [pollMessage, setPollMessage] = useState("");

  const [testingEmail, setTestingEmail] = useState<"alert" | "success" | null>(null);
  const [testEmailStatus, setTestEmailStatus] = useState<"idle" | "ok" | "err">("idle");
  const [testEmailMessage, setTestEmailMessage] = useState("");

  const handleReset = async () => {
    if (!window.confirm(t("admin.dev.reset.confirm"))) return;
    setResetting(true);
    setResetStatus("idle");
    try {
      await resetDevData();
      setResetStatus("ok");
    } catch {
      setResetStatus("err");
    } finally {
      setResetting(false);
      setTimeout(() => setResetStatus("idle"), 3000);
    }
  };

  const handlePoll = async () => {
    setPolling(true);
    setPollStatus("idle");
    setPollMessage("");
    try {
      const data = await runDevFplPoll();
      setPollStatus("ok");
      setPollMessage(data.message ?? "");
    } catch (err) {
      setPollStatus("err");
      setPollMessage(err instanceof ApiError ? err.message : t("admin.fplAutomation.poll.error"));
    } finally {
      setPolling(false);
      setTimeout(() => { setPollStatus("idle"); setPollMessage(""); }, 5000);
    }
  };

  const handleTestEmail = async (kind: "alert" | "success") => {
    setTestingEmail(kind);
    setTestEmailStatus("idle");
    setTestEmailMessage("");
    try {
      const data = await runDevTestEmail(kind);
      setTestEmailStatus("ok");
      setTestEmailMessage(data.message ?? "");
    } catch (err) {
      setTestEmailStatus("err");
      setTestEmailMessage(err instanceof ApiError ? err.message : t("admin.dev.testEmail.error"));
    } finally {
      setTestingEmail(null);
      setTimeout(() => { setTestEmailStatus("idle"); setTestEmailMessage(""); }, 5000);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <h1 style={pageTitle}>{t("admin.dev.title")}</h1>

      <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.2rem" }}>{t("admin.dev.reset.title")}</div>
          <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{t("admin.dev.reset.description")}</div>
        </div>
        <button
          onClick={handleReset}
          disabled={resetting}
          style={{
            ...secondaryBtn,
            borderStyle: "dashed",
            color: resetStatus === "ok" ? "var(--color-success)" : resetStatus === "err" ? "var(--color-danger)" : undefined,
            cursor: resetting ? "wait" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {resetting ? t("admin.dev.reset.resetting") : resetStatus === "ok" ? t("admin.dev.reset.complete") : resetStatus === "err" ? t("admin.dev.reset.failed") : t("admin.dev.reset.btn")}
        </button>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.2rem" }}>{t("admin.fplAutomation.poll.title")}</div>
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>{t("admin.fplAutomation.poll.description")}</div>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <button style={secondaryBtn} onClick={handlePoll} disabled={polling}>
            {polling ? t("admin.fplAutomation.poll.running") : t("admin.fplAutomation.poll.btn")}
          </button>
          {pollMessage && (
            <span style={{ fontSize: "0.88rem", color: pollStatus === "ok" ? "var(--color-status-active-text)" : "var(--color-status-bust-text)" }}>
              {pollMessage}
            </span>
          )}
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.2rem" }}>{t("admin.dev.testEmail.title")}</div>
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>{t("admin.dev.testEmail.description")}</div>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <button style={secondaryBtn} onClick={() => handleTestEmail("alert")} disabled={testingEmail !== null}>
            {testingEmail === "alert" ? t("admin.dev.testEmail.running") : t("admin.dev.testEmail.alertBtn")}
          </button>
          <button style={secondaryBtn} onClick={() => handleTestEmail("success")} disabled={testingEmail !== null}>
            {testingEmail === "success" ? t("admin.dev.testEmail.running") : t("admin.dev.testEmail.successBtn")}
          </button>
          {testEmailMessage && (
            <span style={{ fontSize: "0.88rem", color: testEmailStatus === "ok" ? "var(--color-status-active-text)" : "var(--color-status-bust-text)" }}>
              {testEmailMessage}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
